import type { Member, MemberPermissionType, Role, Team } from '@/types';
import { TeammatePermissionType, MemberPermissionType as WorkspacePermission } from '@/types';

export type WorkspaceDirectoryLeadershipSource = 'access' | 'team';

export interface WorkspaceDirectoryMemberNode {
    childIds: string[];
    customRoles: Role[];
    fgaPolicyCount: number;
    id: string;
    leadershipSource?: WorkspaceDirectoryLeadershipSource;
    member: Member;
    parentId?: string;
    relationshipLabels: string[];
    searchText: string;
    teams: Team[];
}

export interface WorkspaceDirectoryModel {
    membersById: Map<string, Member>;
    nodesById: Map<string, WorkspaceDirectoryMemberNode>;
    rootIds: string[];
}

interface BuildWorkspaceDirectoryModelInput {
    members: Member[];
    roles: Role[];
    teams: Team[];
}

const permissionRank: Record<MemberPermissionType, number> = {
    [WorkspacePermission.OWNER]: 0,
    [WorkspacePermission.ADMIN]: 1,
    [WorkspacePermission.CUSTOM]: 2,
    [WorkspacePermission.MEMBER]: 3,
    [WorkspacePermission.GUEST]: 4,
};

const rankFor = (member: Member) =>
    member.permission ? permissionRank[member.permission] : permissionRank[WorkspacePermission.GUEST] + 1;

export const workspaceMemberDisplayName = (member: Member): string =>
    [member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ') ||
    member.user?.emailAddress ||
    'Workspace Member';

const compareMembers = (left: Member, right: Member) =>
    rankFor(left) - rankFor(right) ||
    workspaceMemberDisplayName(left).localeCompare(workspaceMemberDisplayName(right)) ||
    left.id.localeCompare(right.id);

export const workspaceMemberBelongsToTeam = (member: Member, team: Team): boolean =>
    (team.teammates ?? []).some(
        (teammate) => teammate.member?.id === member.id || teammate.user?.id === member.user?.id,
    );

const workspaceMemberRoles = (member: Member, roles: Role[]): Role[] => {
    const assigned = new Map<string, Role>();

    (member.assignedRoles ?? []).forEach((role) => assigned.set(role.id, role));
    roles.forEach((role) => {
        if ((role.assignedMembers ?? []).some((assignedMember) => assignedMember.id === member.id)) {
            assigned.set(role.id, role);
        }
    });

    return [...assigned.values()].sort((left, right) => left.name.localeCompare(right.name));
};

const teamLeader = (team: Team, members: Member[]): Member | undefined => {
    const teamMembers = members.filter((member) => workspaceMemberBelongsToTeam(member, team));
    const teammateFor = (member: Member) =>
        (team.teammates ?? []).find(
            (teammate) => teammate.member?.id === member.id || teammate.user?.id === member.user?.id,
        );
    const explicitOwner = teamMembers.find(
        (member) => teammateFor(member)?.permission === TeammatePermissionType.OWNER,
    );

    if (explicitOwner) {
        return explicitOwner;
    }

    const owningUser = teamMembers.find((member) => member.user?.id && member.user.id === team.user?.id);
    if (owningUser) {
        return owningUser;
    }

    const explicitAdmin = teamMembers.find(
        (member) => teammateFor(member)?.permission === TeammatePermissionType.ADMIN,
    );

    return explicitAdmin ?? [...teamMembers].sort(compareMembers)[0];
};

const createsCycle = (childId: string, parentId: string, parents: Map<string, string>): boolean => {
    let cursor: string | undefined = parentId;
    const visited = new Set<string>();

    while (cursor && !visited.has(cursor)) {
        if (cursor === childId) {
            return true;
        }
        visited.add(cursor);
        cursor = parents.get(cursor);
    }

    return false;
};

export const buildWorkspaceDirectoryModel = ({
    members,
    roles,
    teams,
}: BuildWorkspaceDirectoryModelInput): WorkspaceDirectoryModel => {
    const orderedMembers = [...members].sort(compareMembers);
    const membersById = new Map(orderedMembers.map((member) => [member.id, member]));
    const memberTeams = new Map(
        orderedMembers.map((member) => [
            member.id,
            [...teams]
                .filter((team) => workspaceMemberBelongsToTeam(member, team))
                .sort((left, right) => left.name.localeCompare(right.name)),
        ]),
    );
    const teamRelationships = new Map<string, Array<{ label: string; parent: Member }>>();

    [...teams]
        .sort((left, right) => left.name.localeCompare(right.name))
        .forEach((team) => {
            const leader = teamLeader(team, orderedMembers);
            if (!leader) {
                return;
            }

            orderedMembers
                .filter((member) => member.id !== leader.id && workspaceMemberBelongsToTeam(member, team))
                .forEach((member) => {
                    const relationships = teamRelationships.get(member.id) ?? [];
                    relationships.push({ label: team.name, parent: leader });
                    teamRelationships.set(member.id, relationships);
                });
        });

    const parents = new Map<string, string>();
    const relationshipLabelsByMember = new Map<string, string[]>();
    const leadershipSourceByMember = new Map<string, WorkspaceDirectoryLeadershipSource>();

    [...orderedMembers]
        .sort((left, right) => compareMembers(right, left))
        .forEach((member) => {
            const relationships = [...(teamRelationships.get(member.id) ?? [])].sort(
                (left, right) => compareMembers(left.parent, right.parent) || left.label.localeCompare(right.label),
            );
            const selected = relationships.find(
                ({ parent }) => parent.id !== member.id && !createsCycle(member.id, parent.id, parents),
            );

            if (selected) {
                parents.set(member.id, selected.parent.id);
                relationshipLabelsByMember.set(
                    member.id,
                    relationships
                        .filter(({ parent }) => parent.id === selected.parent.id)
                        .map(({ label }) => label)
                        .filter((label, index, labels) => labels.indexOf(label) === index),
                );
                leadershipSourceByMember.set(member.id, 'team');
            }
        });

    orderedMembers.forEach((member) => {
        if (parents.has(member.id) || member.permission === WorkspacePermission.OWNER) {
            return;
        }

        const memberRank = rankFor(member);
        const accessParent = orderedMembers
            .filter((candidate) => candidate.id !== member.id && rankFor(candidate) < memberRank)
            .sort((left, right) => rankFor(right) - rankFor(left) || compareMembers(left, right))
            .find((candidate) => !createsCycle(member.id, candidate.id, parents));

        if (accessParent) {
            parents.set(member.id, accessParent.id);
            relationshipLabelsByMember.set(member.id, ['Effective workspace access']);
            leadershipSourceByMember.set(member.id, 'access');
        }
    });

    const childIdsByParent = new Map<string, string[]>();
    parents.forEach((parentId, childId) => {
        const childIds = childIdsByParent.get(parentId) ?? [];
        childIds.push(childId);
        childIdsByParent.set(parentId, childIds);
    });
    childIdsByParent.forEach((childIds) =>
        childIds.sort((leftId, rightId) => compareMembers(membersById.get(leftId)!, membersById.get(rightId)!)),
    );

    const nodesById = new Map<string, WorkspaceDirectoryMemberNode>();
    orderedMembers.forEach((member) => {
        const customRoles = workspaceMemberRoles(member, roles);
        const teamsForMember = memberTeams.get(member.id) ?? [];
        const searchText = [
            workspaceMemberDisplayName(member),
            member.user?.emailAddress,
            member.user?.title,
            member.permission,
            ...customRoles.map(({ name }) => name),
            ...teamsForMember.map(({ name }) => name),
        ]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase();

        nodesById.set(member.id, {
            childIds: childIdsByParent.get(member.id) ?? [],
            customRoles,
            fgaPolicyCount: customRoles.reduce((count, role) => count + (role.permissions?.length ?? 0), 0),
            id: member.id,
            leadershipSource: leadershipSourceByMember.get(member.id),
            member,
            parentId: parents.get(member.id),
            relationshipLabels: relationshipLabelsByMember.get(member.id) ?? [],
            searchText,
            teams: teamsForMember,
        });
    });

    return {
        membersById,
        nodesById,
        rootIds: orderedMembers.filter((member) => !parents.has(member.id)).map(({ id }) => id),
    };
};
